import { useEffect, useState } from 'react'
import { useParams, Link, Navigate } from 'react-router-dom'
import { getConference } from '../lib/api.js'
import { formatDateRange, formatLocalDateTime, formatVenueTime } from '../lib/datetime.js'
import { isPastEvent } from '../lib/dateRange.js'
import { conferenceTags } from '../lib/grouping.js'
import { PAGE_STANDARD } from '../lib/layout.js'
import { placeHref, tagHref, yearHref } from '../lib/permalinks.js'
import ErrorAlert from '../components/ErrorAlert.jsx'
import EventVenueMap from '../components/EventVenueMap.jsx'
import PastPill from '../components/PastPill.jsx'
import TagPill from '../components/TagPill.jsx'
import TalkRow from '../components/TalkRow.jsx'
import { BTN_GHOST, BTN_PRIMARY } from '../lib/ui.js'

// #308: the authoritative view of a conference and its programme. The feed card
// only knows the talks that happened to be on the loaded page; this fetches the
// container with its full published talk list nested.
//
// #343: two changes.
//
// First, a conference holding exactly ONE talk redirects to that talk. Seven of
// the eleven conferences are that shape, and for three of them the talk's title
// is the conference name with a year on it, so the page you landed on was a
// near-empty duplicate whose only real content was a link back out to the page
// you actually wanted. The talk page is the rich one — map, abstract, share,
// .ics — and no single-talk conference carries a registrationUrl, abstract,
// venue or coordinates its talk lacks, so nothing is lost by going there.
//
// The redirect lives HERE rather than on the card's link because
// `ConferenceSummaryDto` carries no talk count: the card's `talks` array holds
// only what landed on the loaded page, and an active filter can shrink it. Only
// this fetch knows the true number.
//
// Second, what remains — real multi-talk conferences, and the private CAB shape
// that publishes a container and no programme — is rendered at the same
// standard as the event detail page rather than markedly plainer than the page
// it links to.

function addressLines(c) {
  return [
    c.venue,
    c.address,
    [c.city, c.region].filter(Boolean).join(', '),
    c.country,
  ].filter(Boolean)
}

function hasCoords(c) {
  return typeof c?.lat === 'number' && typeof c?.lng === 'number'
}

export default function ConferenceDetailPage() {
  const { id } = useParams()
  const [conference, setConference] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    getConference(id)
      .then((data) => { if (!cancelled) setConference(data) })
      .catch((err) => { if (!cancelled) setError(err) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [id])

  if (loading) {
    return (
      <main className={`${PAGE_STANDARD} pb-12 pt-8 sm:pt-10`}>
        <div className="rounded-2xl bg-white p-8 text-center text-slate-500 shadow-sm ring-1 ring-slate-200/60">
          Loading…
        </div>
      </main>
    )
  }

  if (error) {
    return (
      <main className={`${PAGE_STANDARD} pb-12 pt-8 sm:pt-10`}>
        <ErrorAlert
          title="Couldn't load this conference"
          message={error.message || String(error)}
          source="ConferenceDetailPage"
        />
      </main>
    )
  }

  if (!conference) return null

  const talks = conference.talks || []

  // The collapse. `replace` so Back returns to the calendar rather than to this
  // route, which would redirect again and trap the button.
  if (talks.length === 1) {
    return <Navigate to={`/events/${talks[0].id}`} replace />
  }

  const { weekday, monthDay } = formatDateRange(conference.startsAtUtc, conference.endsAtUtc)
  const lines = addressLines(conference)
  const tags = conferenceTags(conference)
  // A conference is over once its END has passed; using the start would mark
  // day two of a two-day event as past while it is still running.
  const past = isPastEvent({ startsAtUtc: conference.endsAtUtc || conference.startsAtUtc })
  const place = placeHref(conference)
  const confYear = conference.startsAtUtc
    ? new Date(conference.startsAtUtc).getUTCFullYear()
    : null

  return (
    <main className={`${PAGE_STANDARD} pb-12 pt-8 sm:pt-10`}>
      <Link to="/" className="text-sm text-slate-500 hover:text-brand-700">← Back to calendar</Link>

      <header className="mt-3">
        <div className="flex flex-wrap items-center gap-2">
          {/* Same brand-blue pill the card uses — it is what makes a container
              read as a container at a glance. */}
          <span className="inline-flex items-center rounded-full bg-brand-100 px-2.5 py-0.5 text-xs font-medium text-brand-800">
            conference
          </span>
          {past ? <PastPill /> : null}
          {conference.format ? (
            <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-700">
              {conference.format.toLowerCase().replace('_', '-')}
            </span>
          ) : null}
          {tags.map((t) => (
            <TagPill key={t.slug || t.name} tag={t} href={tagHref(t)} />
          ))}
          {confYear ? (
            <Link
              to={yearHref(conference.startsAtUtc)}
              className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-700 transition hover:bg-slate-200"
            >
              {confYear}
            </Link>
          ) : null}
        </div>

        <h1 className="mt-2 text-3xl font-bold leading-tight tracking-tight text-slate-900 sm:text-4xl">
          {conference.name}
        </h1>

        {/* A conference is a RANGE, so the range is the headline fact — not the
            single timestamp the event page leads with. */}
        <div className="mt-1 text-base text-slate-500">
          {weekday} · {monthDay}
        </div>

        <dl className="mt-4 grid grid-cols-1 gap-y-1 text-sm sm:grid-cols-2">
          <div>
            <dt className="inline text-slate-500">Your local: </dt>
            <dd className="inline font-medium text-slate-700">{formatLocalDateTime(conference.startsAtUtc)}</dd>
          </div>
          {conference.tzId ? (
            <div>
              <dt className="inline text-slate-500">Venue local: </dt>
              <dd className="inline font-medium text-slate-700">
                {formatVenueTime(conference.startsAtUtc, conference.tzId)} ({conference.tzId})
              </dd>
            </div>
          ) : null}
        </dl>
      </header>

      {conference.abstractText ? (
        <section className="mt-8 rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200/60 sm:p-8">
          <h2 className="text-base font-semibold uppercase tracking-wider text-slate-500">About this conference</h2>
          <div className="mt-3 whitespace-pre-line text-base text-slate-800">
            {conference.abstractText}
          </div>
        </section>
      ) : null}

      {(hasCoords(conference) || lines.length) ? (
        <section className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-[2fr_1fr]">
          {hasCoords(conference)
            ? <EventVenueMap event={{ ...conference, title: conference.name }} />
            : <div />}
          <aside className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200/60">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-500">Where</h3>
            {lines.length ? (
              <address className="mt-2 not-italic text-sm leading-6 text-slate-800">
                {lines.map((line) => (<div key={line}>{line}</div>))}
                {place ? (
                  <Link to={place} className="mt-1 inline-block text-sm text-brand-700 hover:underline">
                    All events in {conference.city} →
                  </Link>
                ) : null}
              </address>
            ) : (
              <p className="mt-2 text-sm text-slate-500">Location TBA.</p>
            )}
          </aside>
        </section>
      ) : null}

      <section className="mt-8 rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200/60 sm:p-8">
        <h2 className="text-base font-semibold uppercase tracking-wider text-slate-500">
          Programme
        </h2>
        {talks.length ? (
          <ul className="mt-2">
            {talks.map((talk) => (
              <TalkRow key={talk.id} talk={talk} year={null} />
            ))}
          </ul>
        ) : (
          // An invitation-only conference publishes its container and nothing
          // else. Say so plainly rather than implying no programme exists.
          <p className="mt-3 text-base italic text-slate-500">
            No talks are listed publicly for this event.
          </p>
        )}
      </section>

      <section className="mt-6 flex flex-wrap items-center gap-2">
        {conference.registrationUrl ? (
          <a
            href={conference.registrationUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={BTN_PRIMARY}
          >
            Register ↗
          </a>
        ) : null}
        {/* #343: the title deliberately does NOT say "the contract for
            /api/v1/conferences". That contract exists and is served, but the
            /contract page still lists only events, users and bookings. */}
        <Link
          to="/contract"
          className={BTN_GHOST}
          title="View the ODCS data contracts for next.actianlabs.com"
        >
          View data contract
        </Link>
      </section>
    </main>
  )
}
